import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErrorType, NoteTemplate, NoteTemplateRequest, NoteTemplateType } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { useAuthContext } from 'views/auth/Auth';
import { Toast } from 'widgets';

export const NoteTemplateQueryKeys = {
    NoteTemplatesQueryKey: (tenantGUID: string) => ['NoteTemplates', tenantGUID] as const,
    NoteTemplateQueryKey: (noteTemplateGUID: string) => ['NoteTemplate', noteTemplateGUID] as const,
} as const;

export const NOTE_TEMPLATE_TYPES: NoteTemplateType[] = [
    NoteTemplateType.GENERAL,
    NoteTemplateType.FINANCIAL_PLAN,
    NoteTemplateType.INVESTMENT_DISCUSSION,
    NoteTemplateType.PROSPECT_CALL,
];

export const useNoteTemplatesQuery = () => {
    const { currentTenant } = useAuthContext();

    return useQuery<NoteTemplate[]>({
        queryKey: NoteTemplateQueryKeys.NoteTemplatesQueryKey(currentTenant.tenantGUID),
        enabled: !!currentTenant.tenantGUID,
        queryFn: async () => {
            const [response] = await CustomerApi().listNoteTemplates(currentTenant.tenantGUID);
            return response || [];
        },
    });
};

export const useCreateNoteTemplateMutation = () => {
    const { currentTenant } = useAuthContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (template: NoteTemplateRequest) => {
            await CustomerApi().createNoteTemplate(currentTenant.tenantGUID, template);
        },
        onSuccess: () => {
            Toast.success('Template created.');
            queryClient.invalidateQueries({
                queryKey: NoteTemplateQueryKeys.NoteTemplatesQueryKey(currentTenant.tenantGUID),
            });
        },
        onError: (e: unknown) => {
            const error = e as { errorType?: ErrorType };
            if (error.errorType === ErrorType.EX_CONFLICT) {
                Toast.info('This note template type already exists. Try to update the existing one.');
            } else {
                Toast.forError(e, 'Failed to create template.');
            }
        },
    });
};

export const useUpdateNoteTemplateMutation = () => {
    const { currentTenant } = useAuthContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            noteTemplateGUID,
            template,
        }: {
            noteTemplateGUID: string;
            template: NoteTemplateRequest;
        }) => {
            await CustomerApi().updateNoteTemplate(noteTemplateGUID, template);
        },
        onSuccess: (_data, variables) => {
            Toast.success('Template updated.');
            queryClient.invalidateQueries({
                queryKey: NoteTemplateQueryKeys.NoteTemplatesQueryKey(currentTenant.tenantGUID),
            });
            queryClient.invalidateQueries({
                queryKey: NoteTemplateQueryKeys.NoteTemplateQueryKey(variables.noteTemplateGUID),
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to update template.');
        },
    });
};

export const useDeleteNoteTemplateMutation = () => {
    const { currentTenant } = useAuthContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (noteTemplateGUID: string) => {
            await CustomerApi().deleteNoteTemplate(noteTemplateGUID);
        },
        onSuccess: (_data, noteTemplateGUID) => {
            Toast.success('Template deleted.');
            queryClient.invalidateQueries({
                queryKey: NoteTemplateQueryKeys.NoteTemplatesQueryKey(currentTenant.tenantGUID),
            });
            queryClient.invalidateQueries({
                queryKey: NoteTemplateQueryKeys.NoteTemplateQueryKey(noteTemplateGUID),
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to delete template.');
        },
    });
};
